import { debounce } from 'es-toolkit';
import { pubsub } from '../pubsub';
import { debugLog, debugError } from '@utils';

/**
 * CartSectionManager - Batches multiple cart section fetches into single requests
 * This prevents the performance issue of multiple parallel requests when cart is updated.
 *
 * Note: Event subscriptions are lazy-initialized only when first subscriber registers.
 * This prevents the manager from interfering with pages that don't need cart functionality.
 */
class CartSectionManager {
  #pendingSections = new Set();
  #subscribers = new Map();
  #isProcessing = false;
  #isInitialized = false;

  /**
   * Lazy-initialize event subscriptions (only on first subscriber)
   */
  #initialize() {
    if (this.#isInitialized) return;
    this.#isInitialized = true;

    // Subscribe to cart events only when first component needs them
    pubsub.subscribe('cart:updated', () => this.#queueSubscribedSections());
    pubsub.subscribe('cart:added', () => this.#queueSubscribedSections());

    debugLog('CartSectionManager: Initialized');
  }

  /**
   * Add all subscribed sections to pending queue and schedule fetch
   */
  #queueSubscribedSections() {
    for (const sectionId of this.#subscribers.keys()) {
      this.#pendingSections.add(sectionId);
    }
    this.#scheduleFetch();
  }

  /**
   * Register a section to be fetched on cart updates
   * @param {string} sectionId - The section ID to fetch
   * @param {function} callback - Callback with the HTML response
   * @returns {function} Unsubscribe function
   */
  subscribe(sectionId, callback) {
    // Lazy initialize on first subscription
    this.#initialize();

    if (!this.#subscribers.has(sectionId)) {
      this.#subscribers.set(sectionId, new Set());
    }
    this.#subscribers.get(sectionId).add(callback);

    // DON'T add to pending sections here - only fetch on actual cart events
    // This prevents unnecessary section fetches on page load

    return () => {
      const subs = this.#subscribers.get(sectionId);
      if (subs) {
        subs.delete(callback);
        if (subs.size === 0) {
          this.#subscribers.delete(sectionId);
        }
      }
    };
  }

  /**
   * Add all subscribed section IDs to the pending set so the next batch
   * fetch includes every section that has at least one active subscriber.
   */
  #enqueueAllSubscribers() {
    for (const sectionId of this.#subscribers.keys()) {
      this.#pendingSections.add(sectionId);
    }
  }

  /**
   * Request a section fetch (will be batched with other requests)
   * @param {string} sectionId - The section ID to fetch
   */
  requestSection(sectionId) {
    this.#pendingSections.add(sectionId);
    this.#scheduleFetch();
  }

  /**
   * Schedule a batched fetch (debounced to collect multiple requests)
   */
  #scheduleFetch = debounce(() => {
    this.#processBatch();
  }, 50);

  /**
   * Process all pending section fetches in a single request
   */
  async #processBatch() {
    if (this.#isProcessing || this.#pendingSections.size === 0) return;

    this.#isProcessing = true;
    const sectionsToFetch = [...this.#pendingSections];
    this.#pendingSections.clear();

    try {
      const sectionsParam = sectionsToFetch.join(',');
      const response = await fetch(`${Shopify.routes.root}?sections=${sectionsParam}`);
      const data = await response.json();

      // Notify all subscribers with their respective section HTML
      for (const sectionId of sectionsToFetch) {
        const sectionHtml = data[sectionId];
        if (sectionHtml && this.#subscribers.has(sectionId)) {
          const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
          this.#subscribers.get(sectionId).forEach((callback) => {
            try {
              callback(html);
            } catch (error) {
              debugError(`Error in section callback for ${sectionId}:`, error);
            }
          });
        }
      }

      debugLog(`CartSectionManager: Fetched ${sectionsToFetch.length} sections in single request`);
    } catch (error) {
      debugError('CartSectionManager: Failed to fetch sections', error);
    } finally {
      this.#isProcessing = false;

      // Process any sections that were added during fetch
      if (this.#pendingSections.size > 0) {
        this.#scheduleFetch();
      }
    }
  }
}

// Export singleton instance
export const cartSectionManager = new CartSectionManager();
